import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { GET_PROFILE_ATTRIBUTES, CREATE_PROFILE_ATTRIBUTE } from '~/services/ProfileAttributes';
import { StandardField, UserAttribute, TbitIntegration } from './types';
import { ProfileAttributeDialog, ProfileAttribute } from '~/components/screens/teams/components/dialogs/ProfileAttributeDialog';
import { 
  TextIcon, 
  NumberIcon, 
  CheckboxIcon, 
  DateIcon, 
  DropdownIcon,
  OpenListIcon,
  EmailIcon,
  UserIcon,
  DocumentIcon,
  DepartmentIcon,
  ChevronDownIcon,
  FileIcon,
  ApiIcon
} from './Icons';

interface LeftPanelProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  expandedSections: {
    standardFields: boolean;
    contactInfo: boolean;
    tbitIntegrations: boolean;
  };
  toggleSection: (section: 'standardFields' | 'contactInfo' | 'tbitIntegrations') => void;
  agentId?: string;
  onFieldClick: (field: StandardField | UserAttribute | TbitIntegration) => void;
}

const LeftPanel: React.FC<LeftPanelProps> = ({
  searchTerm,
  setSearchTerm,
  expandedSections,
  toggleSection,
  agentId,
  onFieldClick
}) => {
  const { t } = useTranslation('forms');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Obtener características reales del endpoint
  const { data: attributesData, loading: attributesLoading, refetch } = useQuery(GET_PROFILE_ATTRIBUTES, {
    variables: { data: { agent_id: agentId } },
    skip: !agentId,
  });

  // Mutación para crear nueva característica
  const [createProfileAttribute, { loading: createLoading }] = useMutation(CREATE_PROFILE_ATTRIBUTE);

  // Standard Fields
  const standardFields: StandardField[] = [
    { id: 'text', type: 'standard', name: t('fields.text'), icon: <TextIcon />, inputType: 'text' },
    { id: 'number', type: 'standard', name: t('fields.number'), icon: <NumberIcon />, inputType: 'number' },
    { id: 'boolean', type: 'standard', name: t('fields.boolean'), icon: <CheckboxIcon />, inputType: 'checkbox' },
    { id: 'date', type: 'standard', name: t('fields.date'), icon: <DateIcon />, inputType: 'date' },
    { id: 'closelist', type: 'standard', name: t('fields.closelist'), icon: <DropdownIcon />, inputType: 'closelist' },
    { id: 'openlist', type: 'standard', name: t('fields.openlist'), icon: <OpenListIcon />, inputType: 'openlist' },
    { id: 'email', type: 'standard', name: t('fields.email'), icon: <EmailIcon />, inputType: 'email' },
    { id: 'file', type: 'standard', name: t('fields.file'), icon: <FileIcon />, inputType: 'file' }
  ];

  // Características del usuario del endpoint
  const profileAttributes = attributesData?.get_profile_attributes?.data || [];
  const userAttributes: UserAttribute[] = profileAttributes.map((attr: any) => ({
    id: attr.id || attr._id,
    type: 'attribute',
    name: attr.name,
    icon: getIconForDataType(attr.dataType),
    dataType: attr.dataType,
    description: attr.description,
    useAI: attr.useAI,
    // Extraer correctamente la estructura de closelist
    closelist: attr.closelist?.options || [],
    multiple: attr.closelist?.multiple || false
  }));

  // T-BIT Integrations
  const tbitIntegrations: TbitIntegration[] = [
    { id: 'api', type: 'integration', name: t('integrations.api'), icon: <ApiIcon />, inputType: 'api' }
  ];

  // Función para obtener el ícono según el tipo de dato
  function getIconForDataType(dataType: string) {
    switch (dataType) {
      case 'text':
        return <TextIcon />;
      case 'number':
        return <NumberIcon />;
      case 'boolean':
        return <CheckboxIcon />;
      case 'date':
        return <DateIcon />;
      case 'closelist':
        return <DropdownIcon />;
      case 'openlist':
        return <OpenListIcon />;
      case 'email':
        return <EmailIcon />;
      case 'file':
        return <FileIcon />;
      default:
        return <UserIcon />;
    }
  }

  const filteredStandardFields = standardFields.filter(field =>
    field.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUserAttributes = userAttributes.filter(attr =>
    attr.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTbitIntegrations = tbitIntegrations.filter(integration =>
    integration.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Manejar la creación de nueva característica
  const handleCreateCharacteristic = async (newAttribute: ProfileAttribute) => {
    if (!agentId) return;

    try {
      await createProfileAttribute({
        variables: {
          data: {
            agent_id: agentId,
            name: newAttribute.name,
            dataType: newAttribute.dataType,
            description: newAttribute.description,
            useAI: newAttribute.useAI,
            ...(newAttribute.dataType === 'closelist' && newAttribute.closelist
              ? { closelist: newAttribute.closelist }
              : {}),
          },
        },
      });

      // Refrescar la lista de características
      await refetch();
      
      // Cerrar el modal
      setShowCreateDialog(false);
    } catch (error) {
      console.error('Error creating characteristic:', error);
      // Aquí podrías mostrar un toast de error si tienes un sistema de notificaciones
    }
  };

  return (
    <div className="w-full lg:w-80 bg-white lg:border-r border-gray-200 lg:fixed lg:left-0 lg:top-0 h-full lg:h-screen overflow-y-auto z-10 lg:ml-20">
      <div className="p-4">
        {/* Search */}
        <div className="relative mb-6">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder={t('search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

      {/* Standard Fields Section */}
      <div className="mb-8">
        <button 
          onClick={() => toggleSection('standardFields')}
          className="flex items-center justify-between w-full text-left mb-3 hover:bg-gray-50 p-2 rounded-md transition-colors"
        >
          <h3 className="text-sm font-semibold text-gray-900">{t('sections.standard_fields')}</h3>
          <div className={`transform transition-transform ${expandedSections.standardFields ? 'rotate-180' : ''}`}>
            <ChevronDownIcon />
          </div>
        </button>
        
        {expandedSections.standardFields && (
          <div>
            {filteredStandardFields.map((field, index) => (
              <div
                key={field.id}
                onClick={() => onFieldClick(field)}
                className="flex items-center py-2 px-3 mb-2 bg-gray-50 hover:bg-blue-100 rounded-md cursor-pointer border border-gray-200 transition-colors"
              >
                <div className="flex items-center text-gray-600 mr-2">
                  {field.icon}
                </div>
                <span className="text-xs font-medium text-gray-700">{field.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User Characteristics Section */}
      <div className="mb-8">
        <button 
          onClick={() => toggleSection('contactInfo')}
          className="flex items-center justify-between w-full text-left mb-3 hover:bg-gray-50 p-2 rounded-md transition-colors"
        >
          <div className="flex items-center">
            
            <h3 className="text-sm font-semibold text-gray-900">{t('sections.user_characteristics')}</h3>
          </div>
          <div className={`transform transition-transform ${expandedSections.contactInfo ? 'rotate-180' : ''}`}>
            <ChevronDownIcon />
          </div>
        </button>

        {expandedSections.contactInfo && (
          <div>
            {attributesLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="text-xs text-gray-500">{t('loading.characteristics')}</div>
              </div>
            ) : userAttributes.length === 0 ? (
              <div className="text-center py-4">
                <div className="text-xs text-gray-500 mb-2">{t('messages.no_characteristics')}</div>
                <div className="text-xs text-gray-400">{t('messages.create_characteristics_hint')}</div>
              </div>
            ) : (
              filteredUserAttributes.map((attr, index) => (
                <div
                  key={attr.id}
                  onClick={() => onFieldClick(attr)}
                  className="flex items-center justify-between py-2 px-3 mb-2 bg-gray-50 hover:bg-blue-100 rounded-md cursor-pointer border border-gray-200 transition-colors"
                >
                  <span className="text-xs font-medium text-gray-700">{attr.name}</span>
                  <div 
                    className={`flex items-center justify-center w-4 h-4 rounded-full tooltip ${
                      attr.useAI 
                        ? 'bg-blue-100 text-blue-600 tooltip-left' 
                        : 'bg-gray-100 text-gray-600 tooltip-left'
                    }`}
                    data-tip={attr.useAI ? t('messages.ai_filled') : t('messages.manual_filled')}
                  >
                    {attr.useAI ? <SparklesIcon className="w-3 h-3" /> : <UserIcon className="w-3 h-3" />}
                  </div>
                </div>
              ))
            )}

            {/* Create New Characteristic Button - moved inside the accordion */}
            <button 
              onClick={() => setShowCreateDialog(true)}
              disabled={!agentId || createLoading}
              className="w-full flex items-center justify-center px-3 py-2 mt-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md text-xs font-medium transition-colors"
            >
              {createLoading ? (
                <>
                  <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('loading.creating')}
                </>
              ) : (
                <>
                  <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('buttons.create_new_characteristic')}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* T-BIT Integrations Section */}
      <div className="mb-8">
        <button 
          onClick={() => toggleSection('tbitIntegrations')}
          className="flex items-center justify-between w-full text-left mb-3 hover:bg-gray-50 p-2 rounded-md transition-colors"
        >
          <h3 className="text-sm font-semibold text-gray-900">{t('sections.tbit_integrations')}</h3>
          <div className={`transform transition-transform ${expandedSections.tbitIntegrations ? 'rotate-180' : ''}`}>
            <ChevronDownIcon />
          </div>
        </button>

        {expandedSections.tbitIntegrations && (
          <div>
            {filteredTbitIntegrations.map((integration, index) => (
              <div
                key={integration.id}
                onClick={() => onFieldClick(integration)}
                className="flex items-center py-2 px-3 mb-2 bg-gray-50 hover:bg-blue-100 rounded-md cursor-pointer border border-gray-200 transition-colors"
              >
                <div className="flex items-center text-gray-600 mr-2">
                  {integration.icon}
                </div>
                <span className="text-xs font-medium text-gray-700">{integration.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal para crear nueva característica */}
      <ProfileAttributeDialog
        isOpen={showCreateDialog}
        isEdit={false}
        onClose={() => setShowCreateDialog(false)}
        onSubmit={handleCreateCharacteristic}
      />
      </div>
    </div>
  );
};

export default LeftPanel;
